import { Component, inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivationStart, NavigationStart, Router, Event, RouterOutlet } from '@angular/router';
import { routerAnimation } from './shared/animations';
import { distinct, distinctUntilChanged, filter, map, mergeMap } from 'rxjs/operators';
import { QuestionFacade } from './core/facade/question.facade';
import { Observable, combineLatest, of } from 'rxjs';
import { ScriptInjectorService } from './core/service/script-injector.service';
import { LayoutComponent } from './core/layout/layout.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [routerAnimation()],
  imports: [LayoutComponent, RouterOutlet],
})
export class AppComponent implements OnInit {
  translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly questionFacade = inject(QuestionFacade);
  private readonly scriptInjector = inject(ScriptInjectorService);
  step: number;
  navigationStart: Observable<Event>;

  constructor() {
    this.navigationStart = this.router.events.pipe(
      filter(this.filterNavigationStartOnPopState),
      map((event: NavigationStart) => event),
    );

    this.setHtmlLang(this.translate.currentLang);

    this.translate.onLangChange.subscribe((lang) => {
      this.setHtmlLang(lang.lang);
    });

    this.router.events.pipe(distinctUntilChanged()).subscribe(() => {
      const newStep = this.router.currentNavigation()?.extras.state?.step;
      // Set step only when needed otherwise animation between steps doesn't work
      if (newStep >= 0 && newStep !== this.step) {
        this.step = this.router.currentNavigation()?.extras.state?.step;
      }
    });

    this.router.events
      .pipe(
        filter(this.isEventActivationStart),
        distinctUntilChanged(),
        map((event) => combineLatest([of(event), this.navigationStart])),
        mergeMap((item) => item),
        distinct(([, navigationStart]) => navigationStart['id']),
      )
      .subscribe(() => this.questionFacade.previous());
  }

  ngOnInit(): void {
    this.scriptInjector.injectScripts();
  }

  private filterNavigationStartOnPopState(event: Event): boolean {
    return event instanceof NavigationStart && event.navigationTrigger === 'popstate';
  }

  private setHtmlLang(lang: string): void {
    document.documentElement.lang = lang;
  }

  isEventActivationStart(event: Event): event is ActivationStart {
    return event instanceof ActivationStart;
  }
}
